import React from 'react';
import { IconBrandTelegram, IconRefresh } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { creatorService } from '../../../services/api';
import { Button } from '../../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../../ui/dialog';
import { ConfirmDialog } from '../../ui/confirm-dialog';

const REASON_LABELS = {
  exact_twitter_username: 'Exact X username',
  exact_creator_name: 'Exact creator name',
  title_contains_twitter_username: 'Group title contains X username',
  title_contains_creator_name: 'Group title contains creator name',
  name_token_overlap: 'Name words overlap'
};

function suggestionLabel(suggestion) {
  const name = suggestion.creatorName || 'Unnamed creator';
  return suggestion.twitterUsername ? `${name} (@${suggestion.twitterUsername})` : name;
}

export default function TelegramGroupMatcher() {
  const [open, setOpen] = React.useState(false);
  const [groups, setGroups] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('active');
  const [selected, setSelected] = React.useState({});
  const [pending, setPending] = React.useState(null);
  const [assigning, setAssigning] = React.useState(false);

  const loadGroups = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await creatorService.getUnmatchedTelegramGroups(statusFilter);
      setGroups(Array.isArray(result) ? result : (result.groups || []));
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Failed to load unmatched Telegram groups.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  React.useEffect(() => {
    if (open) loadGroups();
  }, [loadGroups, open]);

  const requestMatch = (group) => {
    const suggestion = (group.creatorSuggestions || []).find(item => item.creatorId === selected[group.chatId]);
    if (suggestion) setPending({ group, suggestion });
  };

  const confirmMatch = async () => {
    if (!pending || assigning) return;
    setAssigning(true);
    try {
      await creatorService.setTelegramNotificationOverride(pending.suggestion.creatorId, pending.group.chatId);
      setGroups(current => current.filter(group => group.chatId !== pending.group.chatId));
      setSelected(current => {
        const next = { ...current };
        delete next[pending.group.chatId];
        return next;
      });
      toast.success('Telegram group matched to creator');
      setPending(null);
    } catch (requestError) {
      toast.error(requestError.response?.data?.message || 'Failed to match Telegram group.');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" className="min-h-11" onClick={() => setOpen(true)}>
        <IconBrandTelegram className="h-4 w-4 sm:mr-2" aria-hidden="true" />
        <span className="hidden sm:inline">Telegram groups</span>
        <span className="sr-only sm:hidden">Unmatched Telegram groups</span>
      </Button>

      <Dialog open={open} onOpenChange={(nextOpen) => { if (!assigning) setOpen(nextOpen); }}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Unmatched Telegram groups</DialogTitle>
            <DialogDescription>
              Review advisory creator suggestions, select one, then confirm the match. Nothing is matched automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end justify-between gap-3">
            <label className="text-xs font-medium text-text-secondary">
              Group status
              <select
                aria-label="Telegram group status"
                value={statusFilter}
                onChange={(event) => { setGroups([]); setStatusFilter(event.target.value); }}
                disabled={loading || assigning}
                className="mt-1 block h-11 border border-glass-border bg-surface-2 px-3 text-sm text-text-primary"
              >
                <option value="active">Active</option>
                <option value="unavailable">Unavailable</option>
                <option value="all">All</option>
              </select>
            </label>
            <Button type="button" variant="outline" onClick={loadGroups} disabled={loading || assigning}>
              <IconRefresh className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
              Refresh
            </Button>
          </div>

          {error && <div role="alert" className="border border-error/40 bg-error/10 p-3 text-sm text-error">{error}</div>}
          {loading && groups.length === 0 && <p role="status" className="py-8 text-center text-sm text-text-secondary">Loading Telegram groups…</p>}
          {!loading && !error && groups.length === 0 && (
            <p className="border border-glass-border bg-surface-1 p-4 text-sm text-text-secondary">No unmatched Telegram groups were discovered.</p>
          )}

          <div className="space-y-4">
            {groups.map(group => {
              const available = group.status === 'active';
              const suggestions = group.creatorSuggestions || [];
              return (
                <section key={group._id || group.chatId} className="border border-glass-border bg-surface-1 p-4" aria-labelledby={`telegram-group-${group.chatId}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h3 id={`telegram-group-${group.chatId}`} className="font-medium text-text-primary">{group.title || 'Untitled Telegram group'}</h3>
                      <p className="mt-1 font-mono text-xs text-text-secondary">{group.chatId}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 text-xs ${available ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                        {available ? 'Active' : 'Unavailable'}
                      </span>
                      <p className="mt-1 text-xs text-text-tertiary">
                        Bot membership: {group.botMembershipStatus || (group.botIsMember ? 'member' : 'unknown')}
                      </p>
                    </div>
                  </div>

                  {!available && (
                    <p className="mt-3 text-sm text-text-secondary">Restore the bot's group access before matching this destination.</p>
                  )}
                  {available && suggestions.length === 0 && (
                    <p className="mt-3 text-sm text-text-secondary">No safe creator suggestions are available. Add an override from the intended creator's details after verifying the identity.</p>
                  )}
                  {available && suggestions.length > 0 && (
                    <fieldset className="mt-3 space-y-2">
                      <legend className="text-xs font-medium uppercase tracking-wider text-text-tertiary">Suggested creators</legend>
                      {suggestions.map(suggestion => (
                        <label key={suggestion.creatorId} className="flex cursor-pointer items-start gap-3 border border-glass-border bg-surface-2 p-3">
                          <input
                            type="radio"
                            name={`telegram-group-match-${group.chatId}`}
                            value={suggestion.creatorId}
                            checked={selected[group.chatId] === suggestion.creatorId}
                            disabled={loading || assigning}
                            onChange={() => setSelected(current => ({ ...current, [group.chatId]: suggestion.creatorId }))}
                            className="mt-1"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-text-primary">{suggestionLabel(suggestion)}</span>
                            <span className="mt-1 block text-xs text-text-secondary">
                              Suggested match · {Math.round(Number(suggestion.confidence || 0) * 100)}% confidence
                            </span>
                            {suggestion.reasons?.length > 0 && (
                              <span className="mt-1 block text-xs text-text-tertiary">
                                {suggestion.reasons.map(reason => REASON_LABELS[reason] || 'Name similarity').join(' · ')}
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                      <Button
                        type="button"
                        onClick={() => requestMatch(group)}
                        disabled={!selected[group.chatId] || loading || assigning}
                      >
                        Review selected match
                      </Button>
                    </fieldset>
                  )}
                </section>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pending)}
        onOpenChange={(nextOpen) => { if (!nextOpen && !assigning) setPending(null); }}
        onCancel={() => { if (!assigning) setPending(null); }}
        onConfirm={confirmMatch}
        title="Confirm Telegram group match"
        description={pending
          ? `Route creator notifications for ${suggestionLabel(pending.suggestion)} to “${pending.group.title || 'Untitled Telegram group'}” (${pending.group.chatId})? This suggestion is advisory and must be verified by you.`
          : ''}
        confirmText="Confirm match"
        cancelText="Cancel"
        loading={assigning}
      />
    </>
  );
}
